/**
 * Manufacturing feature switches the screens read (`GET
 * /manufacturing-work-orders/features`): whether the saws print stack labels
 * and the floor scans them (GLOBAL setting `manufacturing.saw_labels_enabled`,
 * default off — David 2026-10-20: the saw label printers are not in use).
 * Off: saws need no printer, Start prints nothing, Reprint is hidden, the
 * operator records what the saw made with **Record parts**, and a saw line is
 * finished with its **Finish line** (manufacturing v3). One request per
 * session (5 min stale), shared by every manufacturing screen; while loading
 * (or when the API predates it) the screens assume OFF.
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../client';
import { queryKeys } from '../queryKeys';

export interface ManufacturingFeatures {
  sawLabelsEnabled: boolean;
  /** End-of-line count (2026-10-30): the Count parts tablet page is on. */
  endCountEnabled: boolean;
  /** Manufacturing v3: the completion percentage new work orders start with. */
  completionThresholdPct: number;
  /** The percentages a manager may pick. */
  completionThresholdOptions: number[];
  /** `kitchens.cabinet_serials` is ON: cabinet labels print at the assembly station, numbered. */
  cabinetSerialsOn: boolean;
}

/** The percentages a manager may pick (the API's completion-threshold-setting). */
export const THRESHOLD_OPTIONS = [100, 95, 85, 75, 50] as const;

export const DEFAULT_MANUFACTURING_FEATURES: ManufacturingFeatures = {
  sawLabelsEnabled: false,
  endCountEnabled: false,
  completionThresholdPct: 85,
  completionThresholdOptions: [...THRESHOLD_OPTIONS],
  cabinetSerialsOn: false,
};

export function useManufacturingFeatures() {
  return useQuery({
    queryKey: queryKeys.manufacturingWorkOrders.features,
    queryFn: async () => {
      const { data } = await apiClient.get<ManufacturingFeatures>(
        '/api/manufacturing-work-orders/features',
      );
      return data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** True when saws print stack labels and the floor scans them. */
export function useSawLabelsEnabled(): boolean {
  const { data } = useManufacturingFeatures();
  return (
    data?.sawLabelsEnabled ?? DEFAULT_MANUFACTURING_FEATURES.sawLabelsEnabled
  );
}

/** True when the end-of-line Count parts page is on (GLOBAL `manufacturing.end_count_enabled`). */
export function useEndCountEnabled(): boolean {
  const { data } = useManufacturingFeatures();
  return (
    data?.endCountEnabled ?? DEFAULT_MANUFACTURING_FEATURES.endCountEnabled
  );
}

/**
 * `kitchens.cabinet_serials`: true while ON — label 1 prints at the assembly
 * station when a cabinet is finished, with the cabinet's own number, so the
 * older unnumbered label buttons (a kitchen project's Print labels, a kitchen
 * work order's Print cabinet labels) are hidden; the API refuses them too
 * (409 LABELS_PRINT_AT_ASSEMBLY). `undefined` while the features are loading
 * (or could not be read): not known yet, so those buttons show only on a
 * definite `false` — none appears and then 409s. An API that predates the
 * field → false (it has no such switch).
 */
export function useCabinetSerialsOn(): boolean | undefined {
  const { data } = useManufacturingFeatures();
  if (!data) return undefined;
  return (
    data.cabinetSerialsOn ?? DEFAULT_MANUFACTURING_FEATURES.cabinetSerialsOn
  );
}

/** "Start & print" / "Start" — the one-line verb of a saw line's start. */
export const startVerb = (labelsOn: boolean) =>
  labelsOn ? 'Start & print' : 'Start';

/** "Start all & print" / "Start all". */
export const startAllVerb = (labelsOn: boolean) =>
  labelsOn ? 'Start all & print' : 'Start all';

/** The completion percentage a new work order starts with (85 while loading or on an older API). */
export function useDefaultCompletionThreshold(): number {
  const { data } = useManufacturingFeatures();
  return (
    data?.completionThresholdPct ??
    DEFAULT_MANUFACTURING_FEATURES.completionThresholdPct
  );
}
